if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        // Exakte Suchbegriffe, die direkt zu einem Produkt führen sollen.
        // Nur Handle + Variant-ID hinterlegen – Bild, Preis, Titel etc.
        // werden live über /products/{handle}.js geladen.
        this.exactMatchRedirects = {
          'lack repair set': {
            handle: 'autolack-lackstift',
            variantId: 49784446976328,
          },
        };

        this.cachedMap = new Map();
        this.productCache = new Map();
        this.focusElement = this.input;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
        this.addEventListener('submit', this.onSubmit.bind(this));
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const exactMatch = this.getExactMatch();
          if (exactMatch) {
            this.renderExactMatch(exactMatch);
            return;
          }

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
      }

      getQuery() {
        return this.input.value.trim();
      }

      getExactMatch() {
        const term = this.getQuery().toLowerCase();
        return this.exactMatchRedirects[term] || null;
      }

      buildUrl() {
  const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

  url.searchParams.set('q', this.getQuery());
  url.searchParams.set('resources[type]', 'product,article,page');
  url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
  url.searchParams.set('resources[limit_scope]', 'each');
  url.searchParams.set('section_id', theme.utils.sectionId(this));
  return url;
}
      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();

        const exactMatch = this.getExactMatch();
        if (exactMatch) {
          this.renderExactMatch(exactMatch);
          return;
        }

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      onSubmit(event) {
        const match = this.getExactMatch();
        if (!match) return;

        event.preventDefault();

        const cached = this.productCache.get(match.handle);
        if (cached) {
          window.location.href = `/products/${cached.handle}?variant=${match.variantId}`;
          return;
        }

        this.fetchProduct(match.handle)
          .then((product) => {
            window.location.href = `/products/${product.handle}?variant=${match.variantId}`;
          })
          .catch((error) => console.error(error));
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(id).innerHTML;
      }

      fetchProduct(handle) {
        if (this.productCache.has(handle)) {
          return Promise.resolve(this.productCache.get(handle));
        }

        return fetch(`/products/${handle}.js`)
          .then((response) => response.json())
          .then((product) => {
            this.productCache.set(handle, product);
            return product;
          });
      }

      formatMoney(cents) {
        return (cents / 100).toFixed(2).replace('.', ',') + '€';
      }

      renderExactMatch(match) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        const container = document.getElementById(id);
        if (!container) return;

        this.abortController?.abort();
        this.setAttribute('loading', '');

        this.fetchProduct(match.handle)
          .then((product) => {
            // Falls der Nutzer inzwischen weitergetippt hat, nichts mehr rendern
            if (this.getExactMatch() !== match) return;

            const variant = product.variants.find((v) => v.id === match.variantId);
            if (!variant) throw new Error(`Variant ${match.variantId} not found on product ${match.handle}`);

            const url = `/products/${product.handle}?variant=${variant.id}`;
            const image = (variant.featured_image && variant.featured_image.src) || product.featured_image;
            const title = variant.name || product.title;
            const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

            const priceContainerClass = onSale
              ? 'price price--on-sale flex flex-wrap items-end gap-2 text-sm'
              : 'price flex flex-wrap items-end gap-2 text-sm';

            const priceHtml = onSale
              ? `
                  <span class="sr-only">Verkaufspreis</span>
                  <span class="price__regular whitespace-nowrap">${this.formatMoney(variant.price)}</span>
                  <span class="sr-only">Normaler Preis</span>
                  <span class="price__sale inline-flex items-center h-auto relative">${this.formatMoney(variant.compare_at_price)}</span>
                `
              : `<span class="price__regular whitespace-nowrap">${this.formatMoney(variant.price)}</span>`;

            container.innerHTML = `
              <div class="grid gap-3 md:gap-4 w-full">
                <p class="search__heading block text-xs uppercase leading-tight tracking-widest">Produkte</p>
                <div class="horizontal-products grid">
                  <div class="horizontal-product flex items-center gap-4 md:gap-6 w-full">
                    <a class="horizontal-product__media media media--square media--contain relative overflow-hidden shrink-0" href="${url}" tabindex="-1">
                      <img
                        src="${image}&width=1500"
                        alt="${title}"
                        srcset="${image}&width=180 180w, ${image}&width=360 360w, ${image}&width=540 540w"
                        width="1500"
                        height="1500"
                        loading="lazy"
                      >
                    </a>
                    <div class="horizontal-product__details flex flex-col justify-start gap-1d5">
                      <div class="flex flex-col gap-1">
                        <div class="block">
                          <p class="text-xs text-opacity">${product.vendor}</p>
                          <a href="${url}" class="horizontal-product__title reversed-link font-medium text-base leading-tight">${title}</a>
                        </div>
                      </div>
                      <div class="flex flex-col gap-1">
                        <div class="${priceContainerClass}">
                          ${priceHtml}
                        </div>
                        <div class="text-sm text-gray-500 mt-1">inkl. MwSt., zzgl. Versand</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            `;

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            console.error(error);
            this.removeAttribute('loading');
          });
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}